import React, { FC } from 'react';
import styled from 'styled-components';
import { Course, Match, Player, PlayerResult } from '../../../../database.schema';
import { calculateOveralMatchResult, calculateScore, getLowestNetScoreUsingMatchShots, isMatchFinished } from '../../../utils/score-calculator';

import { PlayerInfo } from './player-info';
import { BACK_NINE_INDEX, FRONT_NINE_INDEX } from '../../../app/constants';
import { useFetchPlayers } from '../../../api/hooks/use-fetch-players';

const ScoreCardWrapper = styled.div`
  width: 100%;
  background: aliceblue;
  margin-bottom: 1rem;
  padding-top: 1rem;
`;

const TableWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-bottom: 1rem;
`;

const Table = styled.table`
  margin: 0;
  border-collapse: collapse;
`;

const TableData = styled.td<{ bgColour?: string | null }>`
  padding: 0.25rem;
  border: 1px solid black;
  text-align: center;
  color: ${(props) => (props.bgColour ? 'white' : 'black')};
  background: ${(props) => props.bgColour || 'white'};
`;
const TableClearData = styled.td`
  padding: 0.25rem;
  color: slategray;
  text-align: center;
`;

const TableResultData = styled.td<{ colour?: string | null }>`
  color: ${(props) => props.colour || 'slategray'};
  padding: 0.25rem;
  text-align: center;
`;

const TableDataRed = styled(TableData)`
  padding: 0.25rem 0.5rem 0.25rem 0.5rem;
  color: red;
`;

const TableHeader = styled.th`
  background-color: #f2f2f2;
  padding: 0.25rem;
  border: 1px solid black;
  color: black;
  text-align: center;
  font-weight: bold;
`;
const TableTotalHeader = styled(TableHeader)`
  border-top: 2px solid black;
  border-bottom: 2px solid black;
`;

const ShotText = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0.25rem 0 0.25rem 1.25rem;
`;

export type Hole = {
  hole: number;
  par: number;
  strokeIndex: number;
  score: number;
  points: number;
};

type ScoreCardProps = {
  startingHole: number;
  recordedMatch: Match;
  course: Course;
  format: 'Pairs' | 'Singles';
};

export const ScoreCard: FC<ScoreCardProps> = ({ startingHole, recordedMatch, course, format }): JSX.Element => {
  const holeIndexes = startingHole === 1 ? [...FRONT_NINE_INDEX, ...BACK_NINE_INDEX] : [...BACK_NINE_INDEX, ...FRONT_NINE_INDEX];
  const { data } = useFetchPlayers();
  const players = data as Array<Player>;
  const receivesAShot = (shotsReceived: number, strokeIndex: number) => {
    return strokeIndex <= shotsReceived ? '*' : '';
  };

  const getHoleResult = (recordedMatch: Match, team: string, holeIndex: number, course: Course): string | null => {
    if (isMatchFinished(recordedMatch.resultByHole, holeIndex)) {
      return null;
    } else {
      const t1LowScore = getLowestNetScoreUsingMatchShots(recordedMatch.team1.players, holeIndex, course.strokeIndex[holeIndexes[holeIndex]]);
      const t2LowScore = getLowestNetScoreUsingMatchShots(recordedMatch.team2.players, holeIndex, course.strokeIndex[holeIndexes[holeIndex]]);
      if (t1LowScore === t2LowScore) {
        return null;
      }
      if (t1LowScore < t2LowScore && team === 't1') {
        return 'red';
      }
      if (t1LowScore > t2LowScore && team === 't2') {
        return 'blue';
      }
      return null;
    }
  };

  const getHoleOverAllResult = (index: number) => {
    const result: string = calculateOveralMatchResult(recordedMatch.resultByHole.slice(0, index));
    if (result === '-- A/S') {
      return <TableResultData>a/s</TableResultData>;
    }
    const colour = result.startsWith('t1') ? 'red' : 'blue';
    return <TableResultData colour={colour}>{result.substring(2).toLowerCase().replaceAll(' ', '')}</TableResultData>;
  };

  return (
    <ScoreCardWrapper data-testid="score-card">
      <PlayerInfo recordedMatch={recordedMatch} players={players} />
      <ShotText>
        <div>* receives a shot</div>
      </ShotText>
      <TableWrapper data-testid="score-card">
        <Table data-testid="score-card-table">
          <thead>
            <tr>
              <TableHeader>Hole</TableHeader>
              <TableHeader>SI</TableHeader>
              <TableHeader>Par</TableHeader>
              {recordedMatch.team1.players.map((player: PlayerResult, index: number) => (
                <TableHeader key={`t1-player-${index}`}>{`P${index + 1}`}</TableHeader>
              ))}
              {recordedMatch.team2.players.map((player: PlayerResult, index: number) => (
                <TableHeader key={`t2-player-${index}`}>{`P${index + recordedMatch.team1.players.length + 1}`}</TableHeader>
              ))}
              <TableResultData></TableResultData>
            </tr>
          </thead>
          <tbody>
            {course &&
              holeIndexes.slice(0, 9).map((holeIndex, index) => (
                <tr key={`front-9-${index}`} data-testid={`front-9-${index}`}>
                  <TableData>{holeIndex + 1}</TableData>
                  <TableDataRed>{course.strokeIndex[holeIndex]}</TableDataRed>
                  <TableData>{course.scoreCard[holeIndex]}</TableData>
                  {recordedMatch.team1.players.map((player: PlayerResult) => (
                    <TableData bgColour={getHoleResult(recordedMatch, 't1', index, course)} key={`1-9-t1-${player.playerId}`}>
                      {`${receivesAShot(player.round.shotsReceived, course.strokeIndex[holeIndex])}${player.round.scoreCard[index]}`}
                      <sup>{`${calculateScore({
                        par: course.scoreCard[holeIndex],
                        score: player.round.scoreCard[index],
                        handicap: player.round.individualHandicap,
                        strokeIndex: course.strokeIndex[holeIndex],
                      })}`}</sup>
                    </TableData>
                  ))}
                  {recordedMatch.team2.players.map((player: PlayerResult) => (
                    <TableData bgColour={getHoleResult(recordedMatch, 't2', index, course)} key={`1-9-t2-${player.playerId}`}>
                      {`${receivesAShot(player.round.shotsReceived, course.strokeIndex[holeIndex])}${player.round.scoreCard[index]}`}
                      <sup>{`${calculateScore({
                        par: course.scoreCard[holeIndex],
                        score: player.round.scoreCard[index],
                        handicap: player.round.individualHandicap,
                        strokeIndex: course.strokeIndex[holeIndex],
                      })}`}</sup>
                    </TableData>
                  ))}
                  {getHoleOverAllResult(index + 1)}
                </tr>
              ))}
            {course && (
              <tr data-testid="out-totals">
                <TableTotalHeader colSpan={2}>Out</TableTotalHeader>
                <TableTotalHeader>{course.scoreCard.slice(0, 9).reduce((acc, hole) => acc + hole, 0)}</TableTotalHeader>
                {recordedMatch.team1.players.map((player: PlayerResult, index: number) => (
                  <TableTotalHeader key={`t19-total-${index}`}>
                    {`${player.round.scoreCard.slice(0, 9).reduce((acc, hole) => acc + hole, 0)}`}
                    <sup>{`${player.round.scoreCard.slice(0, 9).reduce(
                      (acc, hole, index) =>
                        acc +
                        calculateScore({
                          par: course.scoreCard[holeIndexes[index]],
                          score: hole,
                          handicap: player.round.individualHandicap,
                          strokeIndex: course.strokeIndex[holeIndexes[index]],
                        }),
                      0,
                    )}`}</sup>
                  </TableTotalHeader>
                ))}
                {recordedMatch.team2.players.map((player: PlayerResult, index: number) => (
                  <TableTotalHeader key={`t29-total-${index}`}>
                    {`${player.round.scoreCard.slice(0, 9).reduce((acc, hole) => acc + hole, 0)}`}
                    <sup>{`${player.round.scoreCard.slice(0, 9).reduce(
                      (acc, hole, index) =>
                        acc +
                        calculateScore({
                          par: course.scoreCard[holeIndexes[index]],
                          score: hole,
                          handicap: player.round.individualHandicap,
                          strokeIndex: course.strokeIndex[holeIndexes[index]],
                        }),
                      0,
                    )}`}</sup>
                  </TableTotalHeader>
                ))}
                <TableResultData></TableResultData>
              </tr>
            )}
            <tr>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
            </tr>
            {course &&
              holeIndexes.slice(9).map((holeIndex, index) => (
                <tr key={`back-9-${index}`} data-testid={`back-9-${index}`}>
                  <TableData>{holeIndex + 1}</TableData>
                  <TableDataRed>{course.strokeIndex[holeIndex]}</TableDataRed>
                  <TableData>{course.scoreCard[holeIndex]}</TableData>
                  {recordedMatch.team1.players.map((player: PlayerResult) => (
                    <TableData bgColour={getHoleResult(recordedMatch, 't1', index + 9, course)} key={`10-t1-${player.playerId}`}>
                      {`${receivesAShot(player.round.shotsReceived, course.strokeIndex[holeIndex])}${player.round.scoreCard[index + 9]}`}
                      <sup>{`${calculateScore({
                        par: course.scoreCard[holeIndex],
                        score: player.round.scoreCard[index + 9],
                        handicap: player.round.individualHandicap,
                        strokeIndex: course.strokeIndex[holeIndex],
                      })}`}</sup>
                    </TableData>
                  ))}
                  {recordedMatch.team2.players.map((player: PlayerResult) => (
                    <TableData bgColour={getHoleResult(recordedMatch, 't2', index + 9, course)} key={`10-t2-${player.playerId}`}>
                      {`${receivesAShot(player.round.shotsReceived, course.strokeIndex[holeIndex])}${player.round.scoreCard[index + 9]}`}
                      <sup>{`${calculateScore({
                        par: course.scoreCard[holeIndex],
                        score: player.round.scoreCard[index + 9],
                        handicap: player.round.individualHandicap,
                        strokeIndex: course.strokeIndex[holeIndex],
                      })}`}</sup>
                    </TableData>
                  ))}
                  {getHoleOverAllResult(index + 10)}
                </tr>
              ))}
            {course && (
              <tr data-testid="in-totals">
                <TableTotalHeader colSpan={2}>In</TableTotalHeader>
                <TableTotalHeader>{course.scoreCard.slice(9).reduce((acc, hole) => acc + hole, 0)}</TableTotalHeader>
                {recordedMatch.team1.players.map((player: PlayerResult, index: number) => (
                  <TableTotalHeader key={`t1-18-total-${index}`}>
                    {`${player.round.scoreCard.slice(9).reduce((acc, hole) => acc + hole, 0)}`}
                    <sup>{`${player.round.scoreCard.slice(9).reduce(
                      (acc, hole, index) =>
                        acc +
                        calculateScore({
                          par: course.scoreCard[holeIndexes[index + 9]],
                          score: hole,
                          handicap: player.round.individualHandicap,
                          strokeIndex: course.strokeIndex[holeIndexes[index + 9]],
                        }),
                      0,
                    )}`}</sup>
                  </TableTotalHeader>
                ))}
                {recordedMatch.team2.players.map((player: PlayerResult, index: number) => (
                  <TableTotalHeader key={`t2-18-total-${index}`}>
                    {`${player.round.scoreCard.slice(9).reduce((acc, hole) => acc + hole, 0)}`}
                    <sup>{`${player.round.scoreCard.slice(9).reduce(
                      (acc, hole, index) =>
                        acc +
                        calculateScore({
                          par: course.scoreCard[holeIndexes[index + 9]],
                          score: hole,
                          handicap: player.round.individualHandicap,
                          strokeIndex: course.strokeIndex[holeIndexes[index + 9]],
                        }),
                      0,
                    )}`}</sup>
                  </TableTotalHeader>
                ))}
                <TableResultData></TableResultData>
              </tr>
            )}
            <tr>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
              <TableClearData></TableClearData>
            </tr>
            {course && (
              <tr data-testid="overall-totals">
                <TableTotalHeader colSpan={2}>Total</TableTotalHeader>
                <TableTotalHeader>{course.scoreCard.reduce((acc, hole) => acc + hole, 0)}</TableTotalHeader>
                {recordedMatch.team1.players.map((player: PlayerResult, index: number) => (
                  <TableTotalHeader key={`ttt1-9-total-${index}`}>
                    {`${player.round.scoreCard.reduce((acc, hole) => acc + hole, 0)}`}
                    <sup>{`${player.round.scoreCard.reduce(
                      (acc, hole, index) =>
                        acc +
                        calculateScore({
                          par: course.scoreCard[holeIndexes[index]],
                          score: hole,
                          handicap: player.round.individualHandicap,
                          strokeIndex: course.strokeIndex[holeIndexes[index]],
                        }),
                      0,
                    )}`}</sup>
                  </TableTotalHeader>
                ))}
                {recordedMatch.team2.players.map((player: PlayerResult, index: number) => (
                  <TableTotalHeader key={`ttt1-18-total-${index}`}>
                    {`${player.round.scoreCard.reduce((acc, hole) => acc + hole, 0)}`}
                    <sup>{`${player.round.scoreCard.reduce(
                      (acc, hole, index) =>
                        acc +
                        calculateScore({
                          par: course.scoreCard[holeIndexes[index]],
                          score: hole,
                          handicap: player.round.individualHandicap,
                          strokeIndex: course.strokeIndex[holeIndexes[index]],
                        }),
                      0,
                    )}`}</sup>
                  </TableTotalHeader>
                ))}
                <TableResultData></TableResultData>
              </tr>
            )}
          </tbody>
        </Table>
      </TableWrapper>
    </ScoreCardWrapper>
  );
};
